import { getCoreUtils } from "./kCoreHandler";
import { kHTMLCore } from "./kHTMLCore";

/**
 * kHTMLWidgetsCore — server-safe form-control cores for the kHTML
 * lineage. Each is the host-neutral half of a kHTML.ts widget: it holds
 * the widget's STATE and emits a static, print-faithful HTML string
 * (disabled inputs that still render in a headless-browser print-to-PDF),
 * with no DOM and no event wiring. The browser widgets extend these and
 * add the live interaction on top.
 *
 * Split out of kHTMLCore.ts (which holds the structural element/table
 * cores) to keep each file one concern and under the size budget. All
 * extend kHTMLCore, so they nest in a kHtmlDocBuilderCore page the same
 * way every other core does.
 */

/**
 * kProgressBarCore — static two-cell progress bar. Core-safe half of
 * kProgressTable: the percentage clamp + the bar HTML. The browser
 * kProgressTable extends this and adds the clickable DOM bar.
 */
export class kProgressBarCore extends kHTMLCore {
    public DonePerc: number = 0;
    public WidthPx: number = 600;
    public Tooltip: string = "";

    public SetDoneCore = (Perc: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let P: number = Math.floor(Perc);
            if (P < 0) { P = 0; }
            if (P > 100) { P = 100; }
            this.DonePerc = P;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kProgressBarCore.SetDoneCore");
        }
    };

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Visible === false) { return ""; }
            let P: number = Math.floor(this.DonePerc);
            if (P < 0) { P = 0; }
            if (P > 100) { P = 100; }
            let Title: string = this.Tooltip;
            if (Title === "") { Title = String(P) + "%"; }
            const Done: string = "<td style=\"background:blue;"
                + "border-radius:5px;height:12px;color:white;"
                + "text-align:center;width:" + String(P) + "%\">"
                + String(P) + "%</td>";
            let ToDo: string = "";
            if (P < 100) {
                ToDo = "<td style=\"height:12px;width:"
                    + String(100 - P) + "%\"></td>";
            }
            return "<table style=\"width:" + String(this.WidthPx)
                + "px;border-collapse:collapse\" title=\""
                + kHTMLCore.EscapeAttr(Title) + "\"><tr>"
                + Done + ToDo + "</tr></table>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kProgressBarCore.ToHtmlString");
            return "";
        }
    };
}

/**
 * kCheckBoxCore — static checkbox + optional label. Core-safe half of
 * kHTMLCheckBox: emits a disabled checkbox input (renders checked/
 * unchecked in print) so a captured form value prints faithfully.
 */
export class kCheckBoxCore extends kHTMLCore {
    public Checked: boolean = false;
    public LabelText: string = "";

    public SetCheckedCore = (Value: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Checked = Value;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kCheckBoxCore.SetCheckedCore");
        }
    };

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Visible === false) { return ""; }
            let CheckedAttr: string = "";
            if (this.Checked === true) { CheckedAttr = " checked"; }
            let IdAttr: string = "";
            if (this.Id !== "") {
                IdAttr = " id=\"" + kHTMLCore.EscapeAttr(this.Id) + "\"";
            }
            let LabelPart: string = "";
            if (this.LabelText !== "") {
                LabelPart = " <label>"
                    + kHTMLCore.EscapeHtml(this.LabelText) + "</label>";
            }
            return "<span><input type=\"checkbox\" disabled"
                + CheckedAttr + IdAttr + ">" + LabelPart + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kCheckBoxCore.ToHtmlString");
            return "";
        }
    };
}

/**
 * kSelectCore — static <select>. Core-safe half of kSelect: the choices
 * list + which one is selected, emitted as a disabled select that shows
 * the chosen value in print.
 */
export class kSelectCore extends kHTMLCore {
    public Choices: string[] = [];
    public Selected: string = "";
    public Multiple: boolean = false;

    public AddChoiceCore = (Title: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Choices.push(Title);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kSelectCore.AddChoiceCore");
        }
    };

    public AddChoicesCore = (Titles: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Title of Titles) { this.Choices.push(Title); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kSelectCore.AddChoicesCore");
        }
    };

    public SetSelectedCore = (Title: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Selected = Title;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kSelectCore.SetSelectedCore");
        }
    };

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Visible === false) { return ""; }
            let IdAttr: string = "";
            if (this.Id !== "") {
                IdAttr = " id=\"" + kHTMLCore.EscapeAttr(this.Id) + "\"";
            }
            let MultiAttr: string = "";
            if (this.Multiple === true) { MultiAttr = " multiple"; }
            const Parts: string[] = [];
            for (const Choice of this.Choices) {
                let SelAttr: string = "";
                if (Choice === this.Selected) { SelAttr = " selected"; }
                Parts.push("<option" + SelAttr + ">"
                    + kHTMLCore.EscapeHtml(Choice) + "</option>");
            }
            return "<select disabled" + IdAttr + MultiAttr + ">"
                + Parts.join("") + "</select>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kSelectCore.ToHtmlString");
            return "";
        }
    };
}

/**
 * kRadioGroupCore — static radio group. Core-safe half of kRadioGroup:
 * the option labels + the selected one, emitted as disabled radios
 * (horizontal by default, one-per-line when Vertical).
 */
export class kRadioGroupCore extends kHTMLCore {
    public Name: string = "";
    public OptionLabels: string[] = [];
    public Selected: string = "";
    public Vertical: boolean = false;

    public constructor(Name?: string) {
        super();
        if (Name !== undefined) { this.Name = Name; }
    }

    public AddChoiceCore = (Title: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.OptionLabels.push(Title);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kRadioGroupCore.AddChoiceCore");
        }
    };

    public SetSelectedCore = (Title: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Selected = Title;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kRadioGroupCore.SetSelectedCore");
        }
    };

    public ToHtmlString = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Visible === false) { return ""; }
            const NameAttr: string = kHTMLCore.EscapeAttr(this.Name);
            const Parts: string[] = [];
            for (const Label of this.OptionLabels) {
                let CheckedAttr: string = "";
                if (Label === this.Selected) { CheckedAttr = " checked"; }
                const Radio: string = "<input type=\"radio\" name=\""
                    + NameAttr + "\" disabled" + CheckedAttr + "> "
                    + kHTMLCore.EscapeHtml(Label);
                if (this.Vertical === true) {
                    Parts.push("<div><label>" + Radio + "</label></div>");
                } else {
                    Parts.push("<label style=\"margin-right:20px\">"
                        + Radio + "</label>");
                }
            }
            return "<div>" + Parts.join("") + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e,
                "kRadioGroupCore.ToHtmlString");
            return "";
        }
    };
}
